// Shared line/region/valik drawing — the single source of truth for how a scan's annotations
// look, used by the live editing canvas (ImageViewer.jsx) AND by static renders (the saved
// report's foot images, the "Сканирования" preview modal). Before this existed, only the live
// canvas could draw a correct valik; compositeImage.js and generateReport.js either drew nothing
// or drew annotations wrong (e.g. a "region" box rendered as a line with an "undefined мм" label).
export const LINE_COLORS = {
  measure:         "#e53935",
  size_line:       "#1565c0",
  width_line:      "#2e7d32",
  metatarsal_line: "#7b1fa2",
  heel_line:       "#e65100",
};
export const LINE_LABELS = {
  measure:         null,
  size_line:       "Размерная",
  width_line:      "Ширина",
  metatarsal_line: "Метатарзальная",
  heel_line:       "Пяточная",
};
export const FOOT_COLORS = { left: "#3b82f6", right: "#22c55e" };

// Metatarsal pad ("валик") shape — traced from valik.svg (viewBox 0 0 44 57).
const VALIK_PATH_D = "M22 0.5C24.878 0.5 27.6596 1.82336 30.2354 4.05957C32.8107 6.29545 35.1477 9.41778 37.1201 12.9385C41.0698 19.9886 43.5 28.5296 43.5 34.5C43.5 46.3741 33.8741 56 22 56C10.1259 56 0.5 46.3741 0.5 34.5C0.5 28.5296 2.93015 19.9886 6.87988 12.9385C8.85233 9.41778 11.1893 6.29545 13.7646 4.05957C16.3404 1.82336 19.122 0.5 22 0.5Z";
const VALIK_VIEWBOX = { width: 44, height: 57 };

// Intersection of two line SEGMENTS — returns null if they don't actually cross.
function lineIntersect(x1, y1, x2, y2, x3, y3, x4, y4) {
  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(denom) < 1e-10) return null;
  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
  const s = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;
  if (t < 0 || t > 1 || s < 0 || s > 1) return null;
  return { x: x1 + t * (x2 - x1), y: y1 + t * (y2 - y1) };
}

// Where each foot's valik sits, in ORIGINAL scan-pixel space — the single source of truth for
// both drawing it (drawAnnotations below) and hit-testing/dragging it (ImageViewer.jsx). Base
// position is the width_line × metatarsal_line intersection; metatarsal_line.padOffset (px along
// the line's own direction, positive = toward its x2/y2 end) lets it slide up/down that axis
// without moving the lines themselves — 0/undefined reproduces the old fixed-at-intersection
// behavior exactly, so existing saved scans render unchanged.
export function computeFootPads(annotations) {
  const lineAnns = annotations.filter(a => a.type !== "region");
  const regionAnns = annotations.filter(a => a.type === "region");
  const footForPoint = (pt) => {
    for (const r of regionAnns) {
      const lx = Math.min(r.x1, r.x2), rx = Math.max(r.x1, r.x2);
      const yTop = Math.min(r.y1, r.y2), yBot = Math.max(r.y1, r.y2);
      if (pt.x >= lx && pt.x <= rx && pt.y >= yTop && pt.y <= yBot) return r.foot;
    }
    return null;
  };
  const pads = [];
  for (let i = 0; i < lineAnns.length; i++) {
    for (let j = i + 1; j < lineAnns.length; j++) {
      const a = lineAnns[i], b = lineAnns[j];
      const isMetPair = (a.type === "width_line" && b.type === "metatarsal_line") ||
                        (a.type === "metatarsal_line" && b.type === "width_line");
      if (!isMetPair) continue;
      const pt = lineIntersect(a.x1, a.y1, a.x2, a.y2, b.x1, b.y1, b.x2, b.y2);
      if (!pt) continue;
      const foot = footForPoint(pt) ?? (a.foot === b.foot ? a.foot : null);
      if (!foot) continue;
      const met = a.type === "metatarsal_line" ? a : b;
      const metIdx = annotations.indexOf(met);
      const dx = met.x2 - met.x1, dy = met.y2 - met.y1;
      const len = Math.hypot(dx, dy) || 1;
      const ux = dx / len, uy = dy / len;
      const offset = met.padOffset || 0;
      const lineAngle = Math.atan2(dy, dx);
      pads.push({
        foot, metIdx,
        pt: { x: pt.x + ux * offset, y: pt.y + uy * offset },
        axis: { ux, uy },
        rotation: lineAngle - Math.PI / 2,
      });
    }
  }
  return pads;
}

// Resolves which pad size applies to a foot: the explicit per-foot choice, falling back to the
// catalog's first entry — the same default `drawAnnotations` has always silently drawn. Exported
// so generateReport.js's text label can agree with what the image actually shows instead of
// treating an unset choice as "nothing selected".
export function resolvePadSizeId(padSizes, padSizesPerFoot, foot) {
  if (!padSizes?.length) return null;
  return padSizesPerFoot?.[foot] ?? padSizes[0].id;
}

export function regionCorners(ann) {
  const lx = Math.min(ann.x1, ann.x2), rx = Math.max(ann.x1, ann.x2);
  const ty = Math.min(ann.y1, ann.y2), by = Math.max(ann.y1, ann.y2);
  return {
    tl: [lx, ty], tr: [rx, ty], bl: [lx, by], br: [rx, by],
    t: [(lx + rx) / 2, ty], b: [(lx + rx) / 2, by], l: [lx, (ty + by) / 2], r: [rx, (ty + by) / 2],
  };
}

// Draws every annotation (lines + region boxes) plus one metatarsal pad per foot, wherever a
// metatarsal_line × width_line pair intersects. `offsetX/offsetY` shift annotation coordinates
// (which are always in the ORIGINAL scan's coordinate space) onto an already-cropped canvas —
// e.g. a per-foot crop starting at the region's own x1/y1. `scale` converts scan pixels to canvas
// pixels (1 for a full-resolution static render, the live zoom/display ratio for ImageViewer).
export function drawAnnotations(ctx, {
  annotations = [],
  padSizes = [],
  padSizesPerFoot = {},
  dpi = 150,
  scale = 1,
  showLabels = true,
  offsetX = 0,
  offsetY = 0,
  selectedIndex = -1,
  drawRegionBoxes = true,
  activeFoot = null,
  draggingPadFoot = null,
}) {
  const tx = (x) => (x - offsetX) * scale;
  const ty = (y) => (y - offsetY) * scale;

  const drawLine = (ann, highlight) => {
    const { x1, y1, x2, y2, distanceMm, type = "measure" } = ann;
    const color = LINE_COLORS[type] || "#e53935";
    const sx1 = tx(x1), sy1 = ty(y1), sx2 = tx(x2), sy2 = ty(y2);
    ctx.save();
    ctx.strokeStyle = highlight ? "#ff6b00" : color;
    ctx.lineWidth = highlight ? 3 : 2;
    ctx.beginPath(); ctx.moveTo(sx1, sy1); ctx.lineTo(sx2, sy2); ctx.stroke();
    ctx.fillStyle = ctx.strokeStyle;
    [[sx1, sy1], [sx2, sy2]].forEach(([x, y]) => { ctx.beginPath(); ctx.arc(x, y, 4, 0, Math.PI * 2); ctx.fill(); });
    if (showLabels) {
      const mx = (sx1 + sx2) / 2, my = (sy1 + sy2) / 2;
      const prefix = LINE_LABELS[type];
      const label = prefix ? `${prefix}: ${distanceMm} мм` : `${distanceMm} мм`;
      ctx.font = "bold 13px sans-serif";
      const tw = ctx.measureText(label).width;
      ctx.fillStyle = "rgba(0,0,0,0.6)";
      ctx.beginPath(); ctx.roundRect(mx - tw / 2 - 4, my - 16, tw + 8, 20, 4); ctx.fill();
      ctx.fillStyle = "#fff"; ctx.fillText(label, mx - tw / 2, my - 1);
    }
    ctx.restore();
  };

  const drawRegion = (ann, highlight) => {
    if (!drawRegionBoxes) return;
    const lx = Math.min(ann.x1, ann.x2), rx = Math.max(ann.x1, ann.x2);
    const yTop = Math.min(ann.y1, ann.y2), yBot = Math.max(ann.y1, ann.y2);
    const color = FOOT_COLORS[ann.foot] || "#2563EB";
    ctx.save();
    ctx.strokeStyle = highlight ? "#ff6b00" : color;
    ctx.lineWidth = 2;
    ctx.setLineDash(ann.foot === activeFoot ? [] : [8, 4]);
    ctx.strokeRect(tx(lx), ty(yTop), tx(rx) - tx(lx), ty(yBot) - ty(yTop));
    ctx.setLineDash([]);
    Object.values(regionCorners(ann)).forEach(([cx, cy]) => {
      ctx.beginPath(); ctx.arc(tx(cx), ty(cy), 5, 0, Math.PI * 2);
      ctx.fillStyle = highlight ? "#ff6b00" : color; ctx.fill();
    });
    if (showLabels) {
      const label = ann.foot === "left" ? "Левая стопа" : "Правая стопа";
      ctx.font = "bold 12px sans-serif";
      const tw = ctx.measureText(label).width;
      ctx.fillStyle = "rgba(0,0,0,0.6)";
      ctx.beginPath(); ctx.roundRect(tx(lx), ty(yTop) - 20, tw + 8, 18, 3); ctx.fill();
      ctx.fillStyle = "#fff"; ctx.fillText(label, tx(lx) + 4, ty(yTop) - 6);
    }
    ctx.restore();
  };

  annotations.forEach((ann, i) => {
    if (ann.type === "region") drawRegion(ann, i === selectedIndex);
    else drawLine(ann, i === selectedIndex);
  });

  const pads = computeFootPads(annotations);

  for (const foot of ["left", "right"]) {
    const inter = pads.find(x => x.foot === foot);
    if (!inter || !padSizes?.length) continue;
    const metPadPt = inter.pt;
    const padSize = padSizes.find(s => s.id === resolvePadSizeId(padSizes, padSizesPerFoot, foot)) || padSizes[0];
    if (!padSize) continue;
    const lengthPx = (padSize.length_mm / 25.4) * dpi * scale;
    const widthPx = (padSize.width_mm / 25.4) * dpi * scale;
    const cx = tx(metPadPt.x), cy = ty(metPadPt.y);
    const scaleY = lengthPx / VALIK_VIEWBOX.height;
    const scaleX = widthPx / VALIK_VIEWBOX.width;
    const dragging = draggingPadFoot === foot;
    ctx.save();
    ctx.strokeStyle = dragging ? "#ff6b00" : "#7b1fa2";
    ctx.fillStyle = dragging ? "rgba(255, 107, 0, 0.18)" : "rgba(123, 31, 162, 0.12)";
    ctx.translate(cx, cy);
    ctx.rotate(inter.rotation);
    ctx.scale(scaleX, -scaleY);
    ctx.translate(-VALIK_VIEWBOX.width / 2, -VALIK_VIEWBOX.height / 2);
    ctx.lineWidth = 2 / Math.min(scaleX, scaleY);
    ctx.setLineDash([5 / Math.min(scaleX, scaleY), 3 / Math.min(scaleX, scaleY)]);
    const valikPath = new Path2D(VALIK_PATH_D);
    ctx.fill(valikPath);
    ctx.stroke(valikPath);
    ctx.setLineDash([]);
    ctx.restore();
    ctx.save();
    ctx.fillStyle = "#7b1fa2";
    ctx.beginPath(); ctx.arc(cx, cy, 3, 0, Math.PI * 2); ctx.fill();
    if (showLabels) {
      const label = `Валик ${padSize.name} (${padSize.length_mm}×${padSize.width_mm} мм)`;
      ctx.font = "bold 11px sans-serif";
      const tw = ctx.measureText(label).width;
      ctx.fillStyle = "rgba(123, 31, 162, 0.9)";
      ctx.beginPath(); ctx.roundRect(cx - tw / 2 - 3, cy + lengthPx / 2 + 4, tw + 6, 16, 3); ctx.fill();
      ctx.fillStyle = "#fff"; ctx.fillText(label, cx - tw / 2, cy + lengthPx / 2 + 15);
    }
    ctx.restore();
  }
}
